import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Shield, Search, Share2, Activity, FileText, Briefcase, Bot, Plus, RefreshCw, Eye, AlertTriangle, Clock, CheckCircle, XCircle, Globe, Hash, Users, Link2, Target, Filter, Download, Trash2, Play, Square, Settings, Key, Server, Smartphone, Wifi, BookOpen, MessageSquare, Sparkles, Loader2, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import GraphCanvas from "@/components/cyber/GraphCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";

const API = "/api/cyber";

const STATUS_BADGE = {
  OPEN: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  CLOSED: "bg-green-100 text-green-700",
  ARCHIVED: "bg-gray-100 text-gray-700",
  NEW: "bg-blue-100 text-blue-700",
  ACKNOWLEDGED: "bg-amber-100 text-amber-700",
  INVESTIGATING: "bg-purple-100 text-purple-700",
  RESOLVED: "bg-green-100 text-green-700",
  FALSE_POSITIVE: "bg-gray-100 text-gray-700",
  ACTIVE: "bg-green-100 text-green-700",
  DISABLED: "bg-gray-100 text-gray-700",
  PENDING: "bg-amber-100 text-amber-700",
  SUCCESS: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
  RUNNING: "bg-blue-100 text-blue-700",
  LOW: "bg-green-100 text-green-700",
  MEDIUM: "bg-amber-100 text-amber-700",
  HIGH: "bg-orange-100 text-orange-700",
  CRITICAL: "bg-red-100 text-red-700",
};

const ENTITY_TYPES = [
  { value: "IP_ADDRESS", label: "IP Address", icon: Server },
  { value: "DOMAIN", label: "Domain", icon: Globe },
  { value: "URL", label: "URL", icon: Link2 },
  { value: "EMAIL", label: "Email", icon: Hash },
  { value: "HASH", label: "Hash (MD5/SHA)", icon: Hash },
  { value: "PERSON", label: "Person", icon: Users },
  { value: "ORG", label: "Organization", icon: Briefcase },
  { value: "SOCIAL_MEDIA", label: "Social Media", icon: MessageSquare },
  { value: "CRYPTO", label: "Crypto Address", icon: Key },
  { value: "PHONE", label: "Phone", icon: Smartphone },
  { value: "CUSTOM", label: "Custom", icon: Target },
];

const TRANSFORM_TYPES = [
  { value: "dns_resolution", label: "Domain to IP" },
  { value: "reverse_dns", label: "IP to Domain" },
  { value: "whois_lookup", label: "Domain WHOIS" },
  { value: "geolocation_ip", label: "IP Geolocation" },
  { value: "virustotal_hash", label: "Hash to VirusTotal" },
  { value: "shodan_ip", label: "IP to Shodan" },
  { value: "email_reputation", label: "Email Reputation" },
  { value: "social_search", label: "Social Search" },
];

const MONITOR_TYPES = [
  { value: "DNS", label: "DNS Change Monitor" },
  { value: "TLS", label: "TLS Certificate" },
  { value: "PORT", label: "Port Scanner" },
  { value: "WHOIS", label: "WHOIS Expiry" },
  { value: "SOCIAL", label: "Social Media" },
  { value: "CRYPTO", label: "Crypto Transaction" },
  { value: "CUSTOM", label: "Custom Monitor" },
];

export default function CyberMonitorHub() {
  const { toast } = useToast();
  const location = useLocation();
  const getTabFromPath = (pathname) => {
    const segment = pathname.split("/").pop();
    if (segment === "dashboard") return "overview";
    return segment || "overview";
  };
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [loading, setLoading] = useState({});

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [cases, setCases] = useState([]);
  const [entities, setEntities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [transforms, setTransforms] = useState([]);
  const [transformLogs, setTransformLogs] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [graphNodes, setGraphNodes] = useState([]);
  const [graphEdges, setGraphEdges] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [aiChat, setAiChat] = useState([]);

  // Dialog states
  const [showNewCase, setShowNewCase] = useState(false);
  const [showNewEntity, setShowNewEntity] = useState(false);
  const [showNewRelationship, setShowNewRelationship] = useState(false);
  const [showNewMonitor, setShowNewMonitor] = useState(false);
  const [showNewEvidence, setShowNewEvidence] = useState(false);
  const [showTransformRun, setShowTransformRun] = useState(false);
  const [showAlertDetail, setShowAlertDetail] = useState(null);
  const [showEntityDetail, setShowEntityDetail] = useState(null);
  const [showEvidenceDetail, setShowEvidenceDetail] = useState(null);

  // Form states
  const [newCase, setNewCase] = useState({ title: "", description: "", severity: "MEDIUM", case_type: "INVESTIGATION", tags: "" });
  const [newEntity, setNewEntity] = useState({ case_id: "", entity_type: "IP_ADDRESS", value: "", label: "", confidence: 75, source: "manual" });
  const [newRelationship, setNewRelationship] = useState({ source_entity_id: "", target_entity_id: "", relationship_type: "RESOLVES_TO", label: "", confidence: 75 });
  const [newMonitor, setNewMonitor] = useState({ name: "", description: "", monitor_type: "DNS", target: "", interval_minutes: 60 });
  const [newEvidence, setNewEvidence] = useState({ case_id: "", title: "", description: "", evidence_type: "OTHER", content: "", metadata: "" });
  const [transformInput, setTransformInput] = useState({ transform_id: "", input_value: "" });
  const [aiQuery, setAiQuery] = useState("");

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // ── Fetch functions ──
  const fetchDashboard = async () => {
    try { const res = await axios.get(`${API}/dashboard`); setDashboard(res.data); }
    catch (e) { console.error("Dashboard fetch failed"); }
  };
  const fetchCases = async () => {
    try { const res = await axios.get(`${API}/cases`); setCases(res.data.cases || []); }
    catch {}
  };
  const fetchEntities = async (caseId = "") => {
    try { const res = await axios.get(`${API}/entities${caseId ? `?case_id=${caseId}` : ""}`); setEntities(res.data.entities || []); }
    catch {}
  };
  const fetchRelationships = async () => {
    try { const res = await axios.get(`${API}/relationships`); setRelationships(res.data.relationships || []); }
    catch {}
  };
  const fetchTransforms = async () => {
    try { const res = await axios.get(`${API}/transforms`); setTransforms(res.data.transforms || []); }
    catch {}
  };
  const fetchTransformLogs = async () => {
    try { const res = await axios.get(`${API}/transforms/logs?limit=30`); setTransformLogs(res.data.logs || []); }
    catch {}
  };
  const fetchMonitors = async () => {
    try { const res = await axios.get(`${API}/monitors`); setMonitors(res.data.monitors || []); }
    catch {}
  };
  const fetchAlerts = async () => {
    try { const res = await axios.get(`${API}/alerts`); setAlerts(res.data.alerts || []); }
    catch {}
  };
  const fetchEvidence = async () => {
    try { const res = await axios.get(`${API}/evidence`); setEvidence(res.data.evidence || []); }
    catch {}
  };
  const fetchGraph = async (caseId = "") => {
    try {
      if (!caseId) { setGraphNodes([]); setGraphEdges([]); return; }
      const res = await axios.get(`${API}/graph/${caseId}`);
      setGraphNodes(res.data.nodes || []);
      setGraphEdges(res.data.edges || []);
    } catch { setGraphNodes([]); setGraphEdges([]); }
  };

  const handleCaseSelect = (caseId) => {
    if (caseId === "_none") {
      setSelectedCaseId("");
      setGraphNodes([]);
      setGraphEdges([]);
      return;
    }
    setSelectedCaseId(caseId);
    if (caseId) fetchGraph(caseId);
  };

  useEffect(() => {
    fetchDashboard(); fetchCases(); fetchEntities(); fetchRelationships();
    fetchTransforms(); fetchTransformLogs(); fetchMonitors(); fetchAlerts(); fetchEvidence();
  }, []);

  // Sync tab when sidebar navigates
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // ── Actions ──
  const handleCreateCase = async () => {
    try {
      const tags = newCase.tags ? newCase.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
      await axios.post(`${API}/cases`, { ...newCase, tags });
      toast({ title: "Case created" }); setShowNewCase(false); fetchCases(); fetchDashboard();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleCreateEntity = async () => {
    try {
      await axios.post(`${API}/entities`, newEntity);
      toast({ title: "Entity created" }); setShowNewEntity(false); fetchEntities(); fetchDashboard();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateRelationship = async () => {
    try {
      await axios.post(`${API}/relationships`, newRelationship);
      toast({ title: "Relationship created" }); setShowNewRelationship(false); fetchRelationships();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateMonitor = async () => {
    try {
      await axios.post(`${API}/monitors`, newMonitor);
      toast({ title: "Monitor created" }); setShowNewMonitor(false); fetchMonitors();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateEvidence = async () => {
    try {
      const metadata = newEvidence.metadata ? JSON.parse(newEvidence.metadata) : {};
      await axios.post(`${API}/evidence`, { ...newEvidence, metadata });
      toast({ title: "Evidence captured" }); setShowNewEvidence(false); fetchEvidence();
    } catch (e) { toast({ title: e.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handleRunTransform = async () => {
    try {
      setLoading(prev => ({ ...prev, transform: true }));
      const res = await axios.post(`${API}/transforms/${transformInput.transform_id}/execute`, { input_value: transformInput.input_value });
      toast({ title: `Transform complete: ${res.data.result_count || 0} results` });
      setShowTransformRun(false); fetchTransformLogs();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
    finally { setLoading(prev => ({ ...prev, transform: false })); }
  };

  const handleRunMonitor = async (id) => {
    try {
      await axios.post(`${API}/monitors/${id}/run`);
      toast({ title: "Monitor executed" }); fetchMonitors(); fetchAlerts();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleUpdateAlert = async (id, data) => {
    try {
      await axios.put(`${API}/alerts/${id}`, data);
      toast({ title: "Alert updated" }); fetchAlerts();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleUpdateCaseStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/cases/${id}/status`, { status, user_id: "current" });
      toast({ title: `Case ${status}` }); fetchCases(); fetchDashboard();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/search?query=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(res.data.results || []);
    } catch (e) { toast({ title: "Search failed", variant: "destructive" }); }
  };

  const handleDelete = async (type, id, refetch) => {
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast({ title: "Deleted" }); refetch();
    } catch (e) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleAiChat = async () => {
    if (!aiQuery.trim()) return;
    const userMsg = { role: "user", content: aiQuery };
    setAiChat(prev => [...prev, userMsg]);
    setAiQuery("");
    try {
      const res = await axios.post(`${API}/ai/chat`, { query: aiQuery });
      const reply = res.data.response || "No response";
      setAiChat(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setAiChat(prev => [...prev, { role: "assistant", content: "AI service unavailable" }]);
    }
  };

  // ── KPI Cards ──
  const kpiCards = [
    { title: "Active Cases", value: dashboard?.active_cases || 0, icon: Briefcase, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    { title: "Total Entities", value: dashboard?.total_entities || 0, icon: Search, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
    { title: "Critical Alerts", value: dashboard?.critical_alerts || 0, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
    { title: "Active Monitors", value: dashboard?.active_monitors || 0, icon: Activity, color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
    { title: "Transforms Run", value: dashboard?.total_transforms || 0, icon: Share2, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
    { title: "Evidence Items", value: dashboard?.total_evidence || 0, icon: FileText, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cyber Monitor Hub</h1>
          <p className="text-muted-foreground">Threat intelligence, investigation & case management</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Quick search entities..."
              className="pl-8"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchCases(); fetchAlerts(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiCards.map(kpi => (
          <Card key={kpi.title} className={`${kpi.bg} ${kpi.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.title}</p>
                  <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                </div>
                <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Search Results ({searchResults.length})</h3>
              <Button variant="ghost" size="sm" onClick={() => setSearchResults([])}><XCircle className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {searchResults.map((r, i) => (
                <div key={i} className="text-sm flex items-center gap-2 p-1 hover:bg-blue-100 rounded">
                  <Badge variant="outline">{r.entity_type || r.type}</Badge>
                  <span className="font-mono">{r.value || r.label || r.title}</span>
                  {r.case_id && <span className="text-xs text-muted-foreground">Case: {r.case_id}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cases">Cases</TabsTrigger>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="graph">Graph</TabsTrigger>
          <TabsTrigger value="transforms">Transforms</TabsTrigger>
          <TabsTrigger value="monitors">Monitors</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
          <TabsTrigger value="ai">AI Assistant</TabsTrigger>
        </TabsList>

        {/* ═══ OVERVIEW TAB ═══ */}
        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-lg">Recent Cases</CardTitle></CardHeader>
              <CardContent>
                {cases.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No cases yet. Create your first investigation case.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {cases.slice(0, 5).map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2 hover:bg-app rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_BADGE[c.severity] || ""}>{c.severity}</Badge>
                          <span className="font-medium text-sm">{c.title}</span>
                        </div>
                        <Badge className={STATUS_BADGE[c.status] || ""}>{c.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Recent Alerts</CardTitle></CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No alerts yet. Configure monitors to generate alerts.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {alerts.slice(0, 5).map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 hover:bg-app rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_BADGE[a.severity] || ""}>{a.severity}</Badge>
                          <span className="text-sm truncate max-w-[200px]">{a.title}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className={STATUS_BADGE[a.status] || ""}>{a.status}</Badge>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowAlertDetail(a)}>
                            <Eye className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Quick Actions:</span>
                <Button variant="outline" size="sm" onClick={() => setShowNewCase(true)}>
                  <Plus className="h-3 w-3 mr-1" /> New Case
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("transforms")}>
                  <Play className="h-3 w-3 mr-1" /> Run Transform
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("monitors")}>
                  <Activity className="h-3 w-3 mr-1" /> New Monitor
                </Button>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("ai")}>
                  <Bot className="h-3 w-3 mr-1" /> AI Investigate
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ CASES TAB ═══ */}
        <TabsContent value="cases" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Investigation Cases</CardTitle>
              <Dialog open={showNewCase} onOpenChange={setShowNewCase}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Case</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Investigation Case</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Title</Label><Input value={newCase.title} onChange={e => setNewCase({...newCase, title: e.target.value})} /></div>
                    <div><Label>Description</Label><Textarea value={newCase.description} onChange={e => setNewCase({...newCase, description: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Severity</Label>
                        <Select value={newCase.severity} onValueChange={v => setNewCase({...newCase, severity: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOW">Low</SelectItem>
                            <SelectItem value="MEDIUM">Medium</SelectItem>
                            <SelectItem value="HIGH">High</SelectItem>
                            <SelectItem value="CRITICAL">Critical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Type</Label>
                        <Select value={newCase.case_type} onValueChange={v => setNewCase({...newCase, case_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INVESTIGATION">Investigation</SelectItem>
                            <SelectItem value="INCIDENT">Incident Response</SelectItem>
                            <SelectItem value="THREAT_HUNT">Threat Hunt</SelectItem>
                            <SelectItem value="COMPLIANCE">Compliance</SelectItem>
                            <SelectItem value="INTEL">Intelligence</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Tags (comma-separated)</Label><Input value={newCase.tags} onChange={e => setNewCase({...newCase, tags: e.target.value})} /></div>
                    <Button onClick={handleCreateCase}>Create Case</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {cases.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No cases. Click "New Case" to create one.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Entities</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cases.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.title}</TableCell>
                        <TableCell><Badge className={STATUS_BADGE[c.severity]}>{c.severity}</Badge></TableCell>
                        <TableCell><Badge className={STATUS_BADGE[c.status]}>{c.status}</Badge></TableCell>
                        <TableCell>{c.case_type}</TableCell>
                        <TableCell>{c.entity_count || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {c.status !== "CLOSED" && c.status !== "ARCHIVED" && (
                              <>
                                {c.status === "OPEN" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUpdateCaseStatus(c.id, "IN_PROGRESS")} title="Start"><Play className="h-3 w-3" /></Button>}
                                {(c.status === "OPEN" || c.status === "IN_PROGRESS") && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUpdateCaseStatus(c.id, "CLOSED")} title="Close"><CheckCircle className="h-3 w-3 text-green-600" /></Button>}
                              </>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete("cases", c.id, fetchCases)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ENTITIES TAB ═══ */}
        <TabsContent value="entities" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Entities</CardTitle>
              <div className="flex gap-2">
                <Select value={newEntity.case_id} onValueChange={v => setNewEntity({...newEntity, case_id: v})}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="Filter by case" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Cases</SelectItem>
                    {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Dialog open={showNewEntity} onOpenChange={setShowNewEntity}>
                  <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> Add Entity</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add Entity</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Type</Label>
                        <Select value={newEntity.entity_type} onValueChange={v => setNewEntity({...newEntity, entity_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ENTITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Value</Label><Input value={newEntity.value} onChange={e => setNewEntity({...newEntity, value: e.target.value})} placeholder="IP, domain, email, hash..." /></div>
                      <div><Label>Label</Label><Input value={newEntity.label} onChange={e => setNewEntity({...newEntity, label: e.target.value})} /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Case ID</Label><Input value={newEntity.case_id} onChange={e => setNewEntity({...newEntity, case_id: e.target.value})} /></div>
                        <div><Label>Confidence (1-100)</Label><Input type="number" min="1" max="100" value={newEntity.confidence} onChange={e => setNewEntity({...newEntity, confidence: parseInt(e.target.value) || 75})} /></div>
                      </div>
                      <div><Label>Source</Label><Input value={newEntity.source} onChange={e => setNewEntity({...newEntity, source: e.target.value})} /></div>
                      <Button onClick={handleCreateEntity}>Add Entity</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {entities.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No entities discovered yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Case</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entities.map(e => (
                      <TableRow key={e.id}>
                        <TableCell><Badge variant="outline">{e.entity_type}</Badge></TableCell>
                        <TableCell className="font-mono text-sm max-w-[200px] truncate">{e.value}</TableCell>
                        <TableCell>{e.label || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <div className="w-16 bg-gray-200 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${e.confidence >= 80 ? "bg-green-500" : e.confidence >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${e.confidence || 0}%` }} />
                            </div>
                            <span className="text-xs">{e.confidence || 0}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{e.source || "-"}</TableCell>
                        <TableCell className="text-xs">{e.case_id?.slice(0, 12) || "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowEntityDetail(e)}><Eye className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete("entities", e.id, fetchEntities)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ GRAPH TAB ═══ */}
        <TabsContent value="graph" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Relationship Graph</CardTitle>
              <div className="flex gap-2">
                <Select value={selectedCaseId} onValueChange={handleCaseSelect}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Select a case to visualize" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Select a case...</SelectItem>
                    {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => { if (selectedCaseId) fetchGraph(selectedCaseId); else fetchRelationships(); }}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-2">
              <GraphCanvas
                nodes={graphNodes}
                edges={graphEdges}
                caseId={selectedCaseId}
              />
            </CardContent>
          </Card>

          {relationships.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">All Relationships ({relationships.length})</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relationships.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs truncate max-w-[120px]">{r.source_entity_id?.slice(0, 16)}</TableCell>
                        <TableCell><Badge variant="outline">{r.relationship_type}</Badge></TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[120px]">{r.target_entity_id?.slice(0, 16)}</TableCell>
                        <TableCell>{r.confidence || "-"}</TableCell>
                        <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete("relationships", r.id, fetchRelationships)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ TRANSFORMS TAB ═══ */}
        <TabsContent value="transforms" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Transform Registry</CardTitle>
              <Dialog open={showTransformRun} onOpenChange={setShowTransformRun}>
                <DialogTrigger asChild><Button size="sm"><Play className="h-4 w-4 mr-2" /> Run Transform</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Execute Transform</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Transform</Label>
                      <Select value={transformInput.transform_id} onValueChange={v => setTransformInput({...transformInput, transform_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select transform" /></SelectTrigger>
                        <SelectContent>
                          {transforms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Input Value</Label>
                      <Textarea value={transformInput.input_value} onChange={e => setTransformInput({...transformInput, input_value: e.target.value})} placeholder="IP, domain, email, hash..." />
                    </div>
                    <Button onClick={handleRunTransform} disabled={loading.transform}>
                      {loading.transform ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...</> : <><Play className="h-4 w-4 mr-2" /> Execute</>}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {transforms.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No transforms registered. Run POST /api/cyber/init to seed defaults.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Input</TableHead>
                      <TableHead>Output</TableHead>
                      <TableHead>Enabled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transforms.map(t => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell><Badge variant="outline">{t.input_type}</Badge></TableCell>
                        <TableCell><Badge variant="outline">{t.output_type}</Badge></TableCell>
                        <TableCell>{t.enabled !== false ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge className="bg-gray-100 text-gray-700">Disabled</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Transform Logs</CardTitle></CardHeader>
            <CardContent>
              {transformLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No transform executions yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transform</TableHead>
                      <TableHead>Input</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Results</TableHead>
                      <TableHead>Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transformLogs.slice(0, 20).map(l => (
                      <TableRow key={l.id}>
                        <TableCell className="text-xs">{l.transform_name || l.transform_id?.slice(0, 12)}</TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[150px]">{l.input_value}</TableCell>
                        <TableCell><Badge className={STATUS_BADGE[l.status]}>{l.status}</Badge></TableCell>
                        <TableCell>{l.result_count || 0}</TableCell>
                        <TableCell className="text-xs">{l.duration_ms ? `${l.duration_ms}ms` : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ MONITORS TAB ═══ */}
        <TabsContent value="monitors" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Monitors</CardTitle>
              <Dialog open={showNewMonitor} onOpenChange={setShowNewMonitor}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Monitor</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Monitor</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input value={newMonitor.name} onChange={e => setNewMonitor({...newMonitor, name: e.target.value})} /></div>
                    <div><Label>Description</Label><Textarea value={newMonitor.description} onChange={e => setNewMonitor({...newMonitor, description: e.target.value})} /></div>
                    <div><Label>Type</Label>
                      <Select value={newMonitor.monitor_type} onValueChange={v => setNewMonitor({...newMonitor, monitor_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONITOR_TYPES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Target</Label><Input value={newMonitor.target} onChange={e => setNewMonitor({...newMonitor, target: e.target.value})} placeholder="domain.com, IP, wallet..." /></div>
                    <div><Label>Interval (minutes)</Label><Input type="number" value={newMonitor.interval_minutes} onChange={e => setNewMonitor({...newMonitor, interval_minutes: parseInt(e.target.value) || 60})} /></div>
                    <Button onClick={handleCreateMonitor}>Create Monitor</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {monitors.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No monitors configured.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Interval</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monitors.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell><Badge variant="outline">{m.monitor_type}</Badge></TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[150px]">{m.target}</TableCell>
                        <TableCell className="text-xs">{m.interval_minutes}min</TableCell>
                        <TableCell>{m.enabled !== false ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge className="bg-gray-100 text-gray-700">Disabled</Badge>}</TableCell>
                        <TableCell className="text-xs">{m.last_run_at ? new Date(m.last_run_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRunMonitor(m.id)} title="Run now"><Play className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete("monitors", m.id, fetchMonitors)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ALERTS TAB ═══ */}
        <TabsContent value="alerts" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Alerts</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchAlerts}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No alerts generated yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Detected</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map(a => (
                      <TableRow key={a.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">{a.title}</TableCell>
                        <TableCell><Badge className={STATUS_BADGE[a.severity]}>{a.severity}</Badge></TableCell>
                        <TableCell><Badge className={STATUS_BADGE[a.status]}>{a.status}</Badge></TableCell>
                        <TableCell className="text-xs">{a.monitor_id?.slice(0, 12) || "-"}</TableCell>
                        <TableCell className="text-xs">{a.detected_at ? new Date(a.detected_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowAlertDetail(a)}><Eye className="h-3 w-3" /></Button>
                            {a.status === "NEW" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUpdateAlert(a.id, { status: "ACKNOWLEDGED" })} title="Acknowledge"><CheckCircle className="h-3 w-3 text-green-600" /></Button>}
                            {a.status === "ACKNOWLEDGED" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUpdateAlert(a.id, { status: "INVESTIGATING" })} title="Investigate"><Eye className="h-3 w-3 text-amber-600" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Alert Detail Dialog */}
          <Dialog open={!!showAlertDetail} onOpenChange={() => setShowAlertDetail(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Alert Detail</DialogTitle></DialogHeader>
              {showAlertDetail && (
                <div className="space-y-3">
                  <div><Label>Title</Label><p className="font-medium">{showAlertDetail.title}</p></div>
                  <div className="flex gap-3">
                    <div><Label>Severity</Label><Badge className={STATUS_BADGE[showAlertDetail.severity]}>{showAlertDetail.severity}</Badge></div>
                    <div><Label>Status</Label><Badge className={STATUS_BADGE[showAlertDetail.status]}>{showAlertDetail.status}</Badge></div>
                  </div>
                  <div><Label>Description</Label><p className="text-sm">{showAlertDetail.description || "No description"}</p></div>
                  {showAlertDetail.source_data && (
                    <div><Label>Source Data</Label><pre className="text-xs bg-slate-100 p-2 rounded max-h-32 overflow-auto">{JSON.stringify(showAlertDetail.source_data, null, 2)}</pre></div>
                  )}
                  <div className="flex gap-2">
                    {showAlertDetail.status === "NEW" && <Button size="sm" onClick={() => { handleUpdateAlert(showAlertDetail.id, { status: "ACKNOWLEDGED" }); setShowAlertDetail(null); }}>Acknowledge</Button>}
                    {showAlertDetail.status === "ACKNOWLEDGED" && <Button size="sm" onClick={() => { handleUpdateAlert(showAlertDetail.id, { status: "INVESTIGATING" }); setShowAlertDetail(null); }}>Investigate</Button>}
                    <Button size="sm" variant="outline" onClick={() => setShowAlertDetail(null)}>Close</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═══ EVIDENCE TAB ═══ */}
        <TabsContent value="evidence" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Digital Evidence</CardTitle>
              <Dialog open={showNewEvidence} onOpenChange={setShowNewEvidence}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> Capture Evidence</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Capture Evidence</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Title</Label><Input value={newEvidence.title} onChange={e => setNewEvidence({...newEvidence, title: e.target.value})} /></div>
                    <div><Label>Description</Label><Textarea value={newEvidence.description} onChange={e => setNewEvidence({...newEvidence, description: e.target.value})} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Type</Label>
                        <Select value={newEvidence.evidence_type} onValueChange={v => setNewEvidence({...newEvidence, evidence_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SCREENSHOT">Screenshot</SelectItem>
                            <SelectItem value="LOG_FILE">Log File</SelectItem>
                            <SelectItem value="EMAIL">Email</SelectItem>
                            <SelectItem value="CODE_SNIPPET">Code Snippet</SelectItem>
                            <SelectItem value="DOCUMENT">Document</SelectItem>
                            <SelectItem value="IMAGE">Image</SelectItem>
                            <SelectItem value="NETWORK_CAPTURE">Network Capture</SelectItem>
                            <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Case ID</Label><Input value={newEvidence.case_id} onChange={e => setNewEvidence({...newEvidence, case_id: e.target.value})} /></div>
                    </div>
                    <div><Label>Content (for integrity hash)</Label><Textarea value={newEvidence.content} onChange={e => setNewEvidence({...newEvidence, content: e.target.value})} /></div>
                    <div><Label>Metadata (JSON)</Label><Textarea value={newEvidence.metadata} onChange={e => setNewEvidence({...newEvidence, metadata: e.target.value})} placeholder='{"key": "value"}' /></div>
                    <Button onClick={handleCreateEvidence}>Capture & Hash</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {evidence.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No evidence captured yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Integrity</TableHead>
                      <TableHead>Case</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evidence.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">{e.title}</TableCell>
                        <TableCell><Badge variant="outline">{e.evidence_type}</Badge></TableCell>
                        <TableCell>
                          {e.integrity_verified ? (
                            <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Verified</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{e.case_id?.slice(0, 12) || "-"}</TableCell>
                        <TableCell className="text-xs">{e.created_at ? new Date(e.created_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowEvidenceDetail(e)}><Eye className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDelete("evidence", e.id, fetchEvidence)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ AI ASSISTANT TAB ═══ */}
        <TabsContent value="ai" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="text-lg">AI Investigation Assistant</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 h-80 overflow-y-auto mb-4 bg-app">
                {aiChat.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Bot className="h-12 w-12 mb-3 opacity-50" />
                    <p className="font-medium">AI Investigation Assistant</p>
                    <p className="text-sm">Ask questions about entities, threats, or investigation steps.</p>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => { setAiQuery("What can you tell me about this IP address?"); }}>Analyze IP</Button>
                      <Button variant="outline" size="sm" onClick={() => { setAiQuery("Suggest next steps for a phishing investigation"); }}>Phishing Steps</Button>
                      <Button variant="outline" size="sm" onClick={() => { setAiQuery("Assess the threat level of a domain used for credential harvesting"); }}>Threat Assess</Button>
                      <Button variant="outline" size="sm" onClick={() => { setAiQuery("Generate a timeline narrative for a data breach case"); }}>Timeline</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {aiChat.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                          msg.role === "user" ? "bg-blue-600 text-white" : "bg-white border"
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={aiQuery}
                  onChange={e => setAiQuery(e.target.value)}
                  placeholder="Ask the investigation assistant..."
                  onKeyDown={e => e.key === "Enter" && handleAiChat()}
                />
                <Button onClick={handleAiChat}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
